import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
	DefaultGlobalWordDocumentTemplate,
	GlobalWordDocumentTemplate,
} from '@models/documents';
import { MatterFileType } from '@models/matter';
import { BaseComponent } from '@core/base.component';
import { CommonModule } from '@angular/common';
import { ContentHeaderComponent } from 'src/app/ui/content-header/content-header.component';
import {
	BUTTON_PRESETS,
	ButtonComponent,
} from 'src/app/ui/button/button.component';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { BusinessWordDocumentsState } from '@state/business/word/business-word-documents/business-word-documents.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { DocxDocumentService } from '@services/docx-document/docx-document.service';
import { UserProfileState } from '@state/user-profile';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { startWith } from 'rxjs/operators';
import {
	getDocumentBusinessId,
	getDocumentIdWithVersion,
} from '@core/util/documents';
import { getDocumentId } from '@core/util/documents';
import { getDocumentNextVersion } from '@core/util/documents';
import {
	CopyTemplateParams,
	PreviewTemplateParams,
} from '@services/docx-document/docx-document.models';
import { WebdavService } from '@services/webdav/webdav.service';
import { take } from 'rxjs/operators';
import { GlobalDocxTemplatesRepo } from '@repos/global-docx-templates/global-docx-templates.repo';
import { DocumentType } from '@services/docx-document/docx-document.models';
import { getBusinessTemplateStorageRefByDocument } from '@core/util/documents';
import { cloneDeep } from 'lodash';
import { WordDocumentStyles } from '@services/docx-document/docx-document.models';
import { UploadService } from '@services/file/upload.service';
import { MatDialog } from '@angular/material/dialog';
import { DocxViewerDialogComponent } from './docx-viewer-dialog/docx-viewer-dialog.component';
import { BusinessState } from '@state/business/business.state';
import { DialogModule } from '@angular/cdk/dialog';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	EditDocumentDrawerComponent,
	EditDocumentdrawerData,
} from 'src/app/private/sale/documents/document/drawer/edit-document-drawer/edit-document-drawer.component';
import { EditDocumentDrawerModeEnum } from 'src/app/private/sale/documents/document/drawer/edit-document-drawer/edit-document-drawer.component';
import { BusinessBaseDocx } from '@models/businesses';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { GlobalWordDocumentsState } from '@state/business/word/global-word-documents/global-word-documents.state';
import { TemplateListItemComponent } from 'src/app/ui/template-list-item/template-list-item.component';

interface VM {
	documents: GlobalWordDocumentTemplate[];
	filteredDocuments: GlobalWordDocumentTemplate[];
	selectedMatterFileType: MatterFileType | 'unassigned';
	selectedStatus: string;
	searchTerm: string;
	documentsOpening: string[];
}

// Add new interface for categorized documents
interface CategorizedDocuments {
	byMatterType: Map<
		MatterFileType | 'unassigned',
		GlobalWordDocumentTemplate[]
	>;
	byStatus: Map<string, GlobalWordDocumentTemplate[]>;
	all: GlobalWordDocumentTemplate[];
}

const defaultVM: VM = {
	documents: [],
	filteredDocuments: [],
	selectedMatterFileType: null,
	selectedStatus: null,
	searchTerm: '',
	documentsOpening: [],
};

@UntilDestroy()
@Component({
	imports: [
		CommonModule,
		ContentHeaderComponent,
		ButtonComponent,
		TemplateListItemComponent,
		IconsModule,
		ReactiveFormsModule,
		DialogModule,
	],
	selector: 'app-docx-templates',
	templateUrl: './docx-templates.component.html',
	styles: [
		`
			:host {
				overflow: auto;
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocxTemplatesComponent
	extends BaseComponent<VM>
	implements OnInit
{
	searchFormControl: FormControl<string>;
	matterFileTypes = MatterFileType;
	activeFilterCount = 0;

	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId!: string;
	@SelectSnapshot(BusinessBaseDocxsState.getBusinessBaseDocxs)
	businessBaseDocxs!: BusinessBaseDocx[];
	@SelectSnapshot(GlobalWordDocumentsState.getDocumentBySource)
	getGlobalDocBySourceFn: (source: string) => GlobalWordDocumentTemplate;
	@Select(BusinessWordDocumentsState.getAllMerged) documents$: Observable<
		GlobalWordDocumentTemplate[]
	>;
	@SelectSnapshot(BusinessState.getBusinessWordDocumentStyles)
	defaultWordDocumentStyles: WordDocumentStyles;

	private documentDropdownItemsMap = new Map<
		string,
		{
			title: { label: string; icon: string };
			action: () => void;
		}[]
	>();

	private categorizedDocs: CategorizedDocuments = {
		byMatterType: new Map(),
		byStatus: new Map(),
		all: [],
	};

	constructor(
		private docxService: DocxDocumentService,
		private webdavService: WebdavService,
		private docxDocumentService: DocxDocumentService,
		private globalDocxTemplatesRepo: GlobalDocxTemplatesRepo,
		private uploadService: UploadService,
		private dialog: MatDialog,
		private drawerService: DrawerService,
	) {
		super(defaultVM);
		this.searchFormControl = new FormControl<string>('');
	}

	ngOnInit(): void {
		this.documents$.pipe(untilDestroyed(this)).subscribe((documents) => {
			this.preComputeDropdownItems(documents);
			this.categorizeDocs(documents);
			this.VM = {
				documents: documents.sort((a, b) => {
					return a.title.localeCompare(b.title);
				}),
			};
			this.applyFilters();
		});
		this.searchFormControl.valueChanges
			.pipe(startWith(''), untilDestroyed(this))
			.subscribe(() => {
				this.applyFilters();
			});
	}

	toggleFileType(type: MatterFileType | 'unassigned'): void {
		this.VM = {
			selectedMatterFileType:
				this.VM.selectedMatterFileType === type ? null : type,
		};
		this.updateFilterCount();
		this.applyFilters();
	}

	toggleStatus(status: string): void {
		this.VM = {
			selectedStatus: this.VM.selectedStatus === status ? null : status,
		};
		this.updateFilterCount();
		this.applyFilters();
	}

	clearFilters(): void {
		this.VM = {
			selectedMatterFileType: null,
			selectedStatus: null,
		};
		this.activeFilterCount = 0;
		this.applyFilters();
	}

	isSelected(type: MatterFileType | 'unassigned'): boolean {
		return this.VM.selectedMatterFileType === type;
	}

	isSelectedStatus(status: string): boolean {
		return this.VM.selectedStatus === status;
	}

	private updateFilterCount(): void {
		this.activeFilterCount =
			(this.VM.selectedMatterFileType ? 1 : 0) +
			(this.VM.selectedStatus ? 1 : 0);
	}

	clearSearch(input: HTMLInputElement): void {
		this.searchFormControl.setValue('');
		input.focus();
	}

	createTemplate(): void {
		this.drawerService.openDrawer(EditDocumentDrawerComponent, {
			title: 'Create Template',
			data: {
				document: {
					...DefaultGlobalWordDocumentTemplate,
					businessId: this.businessId,
				},
				mode: EditDocumentDrawerModeEnum.CREATE,
				baseDocxs: this.businessBaseDocxs,
			} as EditDocumentdrawerData,
			size: 'sm',
		});
	}

	private applyFilters(): void {
		if (!this.VM.documents) return;

		let filtered = this.categorizedDocs.all;

		if (this.VM.selectedMatterFileType) {
			if (this.VM.selectedMatterFileType === 'unassigned') {
				filtered = filtered.filter((doc) => !doc.matterType?.length);
			} else {
				filtered =
					this.categorizedDocs.byMatterType.get(
						this.VM.selectedMatterFileType,
					) || [];
			}
		}

		if (this.VM.selectedStatus) {
			const statusFiltered =
				this.categorizedDocs.byStatus.get(this.VM.selectedStatus) || [];
			filtered = filtered.filter((doc) => statusFiltered.includes(doc));
		}

		const searchTerm = this.searchFormControl.value?.toLowerCase() || '';
		if (searchTerm) {
			filtered = filtered.filter(
				(doc) =>
					doc.title.toLowerCase().includes(searchTerm) ||
					doc.matterType.some((type) =>
						type.toLowerCase().includes(searchTerm),
					),
			);
		}

		this.VM = {
			...this.VM,
			filteredDocuments: filtered,
		};
	}

	trackByDocumentId(
		_index: number,
		document: GlobalWordDocumentTemplate,
	): string {
		return document.id;
	}

	private preComputeDropdownItems(
		documents: GlobalWordDocumentTemplate[],
	): void {
		documents.forEach((document) => {
			const items = [
				{
					title: {
						label: 'Settings',
						icon: 'settings',
					},
					action: () => this.onOpenSettingsDrawer(document),
				},
				{
					title: {
						label: 'Duplicate',
						icon: 'copy-plus',
					},
					action: () => this.docxService.copy(document, this.businessId),
				},
			];

			if (!document.isBasedOnCloserTemplate) {
				items.push({
					title: {
						label: 'Delete',
						icon: 'trash',
					},
					action: () => this.docxService.delete(document),
				});
			}

			this.documentDropdownItemsMap.set(document.id, items);
		});
	}

	getDropdownItems(documentId: string) {
		return this.documentDropdownItemsMap.get(documentId);
	}

	onOpenSettingsDrawer(document: GlobalWordDocumentTemplate): void {
		const globalDoc = this.getGlobalDocBySourceFn(document.source);

		this.drawerService.openDrawer(EditDocumentDrawerComponent, {
			title: 'Template Settings',
			data: {
				document: document,
				globalDocument: globalDoc,
				baseDocxs: this.businessBaseDocxs,
				mode: EditDocumentDrawerModeEnum.EDIT,
			} as EditDocumentdrawerData,
			size: 'sm',
		});
	}

	async openDocumentPreviewModal(
		doc: GlobalWordDocumentTemplate,
	): Promise<void> {
		if (this.VM.documentsOpening.includes(doc.id)) return;
		this.VM = {
			documentsOpening: [...this.VM.documentsOpening, doc.id],
		};
		const clonedDoc = cloneDeep(doc);
		const previousVersionNonversionedId = getDocumentIdWithVersion(
			clonedDoc.source,
			clonedDoc.parentVersion,
			clonedDoc.version,
		);
		const docBizId = getDocumentBusinessId(clonedDoc);
		const currentVersionPath = `word-documents/${docBizId}/${previousVersionNonversionedId}.docx`;

		const docxUrl = await this.uploadService.fileExists(
			`word-documents/${docBizId}`,
			`${previousVersionNonversionedId}.docx`,
		);

		if (docxUrl) {
			const requestPreviewDocx: PreviewTemplateParams = {
				sourcePath: currentVersionPath,
				wordDocumentStyles: clonedDoc?.ignoreBusinessStylingSettings
					? null
					: this.defaultWordDocumentStyles,
				baseDocumentPath: null,
			};

			const preview = await this.docxDocumentService
				.previewTemplate(requestPreviewDocx)
				.pipe(take(1))
				.toPromise();

			this.drawerService.openDrawer(DocxViewerDialogComponent, {
				title: clonedDoc.title,
				size: 'lg',
				noStyle: true,
				data: {
					docxUrl: docxUrl,
					docxBase64: {
						data: preview.base64,
						encoding: 'docx',
					},
					fileName: `${clonedDoc.title}.docx`,
					onEdit: () => {
						this.openDocumentInWord(doc);
						this.drawerService.closeDrawer();
					},
				},
				titleAction: {
					text: 'Edit Template',
					buttonPreset: BUTTON_PRESETS.HIGH_EMPHASIS,
					action: () => {
						this.openDocumentInWord(doc);
						this.drawerService.closeDrawer();
					},
				},
			});
		} else {
			console.log(`Couldn't find file : ${currentVersionPath}`);
		}
		this.VM = {
			documentsOpening: this.VM.documentsOpening.filter(
				(id) => id !== clonedDoc.id,
			),
		};
	}

	private categorizeDocs(documents: GlobalWordDocumentTemplate[]): void {
		this.categorizedDocs = {
			byMatterType: new Map(),
			byStatus: new Map(),
			all: documents,
		};

		documents.forEach((doc) => {
			if (!doc.matterType?.length) {
				if (!this.categorizedDocs.byMatterType.has('unassigned')) {
					this.categorizedDocs.byMatterType.set('unassigned', []);
				}
				this.categorizedDocs.byMatterType.get('unassigned')?.push(doc);
			}

			doc.matterType.forEach((type) => {
				if (!this.categorizedDocs.byMatterType.has(type)) {
					this.categorizedDocs.byMatterType.set(type, []);
				}
				this.categorizedDocs.byMatterType.get(type)?.push(doc);
			});
		});

		const lastUpdatedDocs = documents.filter((doc) => doc.updatedAt);
		this.categorizedDocs.byStatus.set('lastUpdated', lastUpdatedDocs);
	}

	async openDocumentInWord(doc: GlobalWordDocumentTemplate): Promise<void> {
		const clonedDoc = cloneDeep(doc);
		this.VM = {
			documentsOpening: [...this.VM.documentsOpening, clonedDoc.id],
		};

		const previousVersionNonversionedId = getDocumentIdWithVersion(
			clonedDoc.source,
			clonedDoc.parentVersion,
			clonedDoc.version,
		);
		const docBizId = getDocumentBusinessId(clonedDoc);
		const currentVersionPath = `word-documents/${docBizId}/${previousVersionNonversionedId}.docx`;

		if (clonedDoc.businessId === 'global') {
			clonedDoc.versions = [];
		}

		const nextDocumentVersion = getDocumentNextVersion(clonedDoc);
		clonedDoc.version = nextDocumentVersion.version;
		clonedDoc.useGlobal = false;
		clonedDoc.versions.push(nextDocumentVersion);
		clonedDoc.businessId = this.businessId;
		const docId = getDocumentId(this.businessId, clonedDoc.source);

		await this.globalDocxTemplatesRepo.Put(docId, { ...clonedDoc, id: docId });

		const nonHashedIdWithVersion = getDocumentIdWithVersion(
			clonedDoc.source,
			clonedDoc.parentVersion,
			clonedDoc.version,
		);

		const copyTemplateRequest: CopyTemplateParams = {
			sourcePath: currentVersionPath,
			destinationPath: `word-documents/${this.businessId}/${nonHashedIdWithVersion}.docx`,
			title: clonedDoc.title,
			parameters: {
				Context: null,
				MatterId: null,
				DocumentType: DocumentType.BUSINESS_TEMPLATE as string,
			},
			wordDocumentStyles: clonedDoc?.ignoreBusinessStylingSettings
				? null
				: this.defaultWordDocumentStyles,
			baseDocumentPath: null,
		};

		await this.docxDocumentService
			.copyTemplate(copyTemplateRequest)
			.pipe(take(1))
			.toPromise();

		const storageRef = getBusinessTemplateStorageRefByDocument(
			clonedDoc as GlobalWordDocumentTemplate,
		);

		await this.webdavService.openDocumentForEditing(
			storageRef,
			this.businessId,
			null,
			nonHashedIdWithVersion,
			clonedDoc.title,
		);

		// Update the selected document in the VM instead of using documentTreee
		this.VM = {
			documents: this.VM.documents.map((doc) =>
				doc.id === clonedDoc.id ? clonedDoc : doc,
			),
			documentsOpening: this.VM.documentsOpening.filter(
				(id) => id !== clonedDoc.id,
			),
		};
	}
}
